import { Camera, Images } from 'lucide-react';
import { useId } from 'react';
import { errorMessage } from '../../lib/api';
import { useOnline } from '../../lib/appStatus';
import { useUploadPhotos } from '../../lib/closingApi';
import { plural } from '@kominiarz/shared';
import { useToast } from '../Toast';
import styles from './visit.module.css';

/**
 * Two buttons on purpose: `capture` opens the camera directly but hides the gallery
 * on phones, so "take a photo" and "choose from gallery" are separate inputs.
 * Photos upload as soon as they are chosen.
 */
export function PhotoPicker({ visitId }: { visitId: number }) {
  const id = useId();
  const upload = useUploadPhotos(visitId);
  const online = useOnline();
  const blocked = upload.isPending || !online;
  const toast = useToast();

  async function send(list: FileList | null) {
    const files = list ? Array.from(list) : [];
    if (files.length === 0) return;
    try {
      const res = await upload.mutateAsync(files);
      if (res.photos.length > 0) toast.success(`Dodano ${plural(res.photos.length, 'zdjęcie', 'zdjęcia', 'zdjęć')}`);
      if (res.rejected.length > 0) {
        toast.error(res.rejected.map((r) => `${r.name}: ${r.reason}`).join(' '));
      }
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  return (
    <div className={styles.picker}>
      <label className={styles.pickerButton} htmlFor={`${id}-camera`} aria-disabled={blocked}>
        <Camera aria-hidden="true" />
        Zrób zdjęcie
      </label>
      <input
        id={`${id}-camera`}
        className="visually-hidden"
        type="file"
        accept="image/*"
        capture="environment"
        disabled={blocked}
        onChange={(e) => {
          void send(e.target.files);
          e.target.value = '';
        }}
      />
      <label className={styles.pickerButton} htmlFor={`${id}-gallery`} aria-disabled={blocked}>
        <Images aria-hidden="true" />
        Z galerii
      </label>
      <input
        id={`${id}-gallery`}
        className="visually-hidden"
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        disabled={blocked}
        onChange={(e) => {
          void send(e.target.files);
          e.target.value = '';
        }}
      />
      {!online && <p className={styles.uploading}>Zdjęcia dodasz, gdy wróci internet.</p>}
      {upload.isPending && (
        <p className={styles.uploading} role="status">
          Wysyłanie zdjęć… To może chwilę potrwać przy słabym zasięgu.
        </p>
      )}
    </div>
  );
}
